/**
 * 账户设置弹窗 — 100% 还原 HTML 原型 accountMask
 */
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuthStore } from '@/stores/authStore';
import { usePermissionStore } from '@/stores/permissionStore';
import { useToast } from '@/components/Toast';
import { useNavigate } from 'react-router-dom';
import * as api from '@/services/api';
import './AccountSettingsModal.css';

interface AccountSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

type TabId = 'profile' | 'security';

const MODULE_LABELS: Record<string, string> = {
  tools: '超级工具',
  wins: '战果发布',
  knowledge: '企业知识库',
  apps: '企业应用',
  connectors: '连接器',
  org: '部门人员与权限',
};

export function AccountSettingsModal({ open, onClose }: AccountSettingsModalProps) {
  const { user, logout } = useAuthStore();
  const { permissions, clearPermissions } = usePermissionStore();
  const toast = useToast();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabId>('profile');
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);

  // 头像相关
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null); // base64 预览
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const [profileForm, setProfileForm] = useState({
    displayName: '',
    email: '',
    phone: '',
  });

  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // 同步用户数据
  useEffect(() => {
    if (user) {
      setProfileForm({
        displayName: user.name || '',
        email: user.email || '',
        phone: '',
      });
    }
  }, [user]);

  // ESC 关闭
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // 开始编辑时重置表单
  const startEditing = () => {
    setProfileForm({
      displayName: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
    });
    setAvatarPreview(null);
    setAvatarFile(null);
    setIsEditingProfile(true);
  };

  // 取消编辑
  const cancelEditing = () => {
    setIsEditingProfile(false);
    setAvatarPreview(null);
    setAvatarFile(null);
  };

  // 选择头像图片
  const handleAvatarSelect = (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      toast.error('图片不能超过 2MB');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('请选择 JPG、PNG 或 WebP 格式');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
    setAvatarFile(file);
  };

  // 保存资料
  const handleSaveProfile = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!profileForm.displayName.trim()) {
      toast.error('显示名称不能为空');
      return;
    }
    setSaving(true);
    try {
      const updatedUser = await api.updateProfile({
        name: profileForm.displayName.trim(),
        email: profileForm.email.trim() || undefined,
        phone: profileForm.phone.trim() || undefined,
        // 如果选了头像文件，传 base64 data URL
        avatar_url: avatarPreview || undefined,
      });

      // 更新本地用户状态
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        useAuthStore.getState().setUser({
          ...currentUser,
          name: updatedUser?.name || profileForm.displayName,
          email: updatedUser?.email || profileForm.email,
          phone: updatedUser?.phone ?? (profileForm.phone.trim() || currentUser.phone),
          avatar_text: updatedUser?.avatar_text,
          avatar_url: updatedUser?.avatar_url || avatarPreview || currentUser.avatar_url,
        });
      }

      toast.success('资料保存成功');
      setIsEditingProfile(false);
      setAvatarPreview(null);
      setAvatarFile(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  // 修改密码
  const handleChangePassword = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!passwordForm.oldPassword) {
      toast.error('请输入当前密码');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error('新密码至少需要 8 位');
      return;
    }
    // 校验密码复杂度（与后端一致：大写+小写+数字）
    if (!/[A-Z]/.test(passwordForm.newPassword)) {
      toast.error('新密码必须包含至少一个大写字母');
      return;
    }
    if (!/[a-z]/.test(passwordForm.newPassword)) {
      toast.error('新密码必须包含至少一个小写字母');
      return;
    }
    if (!/[0-9]/.test(passwordForm.newPassword)) {
      toast.error('新密码必须包含至少一个数字');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('两次输入的密码不一致');
      return;
    }
    setChangingPwd(true);
    try {
      await api.changePassword({
        old_password: passwordForm.oldPassword,
        new_password: passwordForm.newPassword,
      });
      toast.success('密码修改成功，请重新登录');
      setIsChangingPassword(false);
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '密码修改失败');
    } finally {
      setChangingPwd(false);
    }
  };

  // 退出登录
  const handleLogout = () => {
    clearPermissions();
    logout();
    onClose();
    navigate('/login');
    toast.success('已退出登录');
  };

  const getAvatarText = () => (user?.name ? user.name.charAt(0).toUpperCase() : '?');
  const avatarBg = user?.avatar_color || '#6366f1';
  const avatarUrl = (user as any)?.avatar_url as string | undefined;

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'superadmin': return '超级管理员';
      case 'admin': return '管理员';
      case 'user': return '成员';
      case 'guest': return '访客';
      default: return role || '成员';
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className={`account-mask${open ? ' open' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      aria-hidden={!open}
    >
      <div
        className="account-box"
        role="dialog"
        aria-modal="true"
        aria-labelledby="accountTitle"
      >
        {/* Header */}
        <header className="account-head">
          <div className="account-head-copy">
            <h3 id="accountTitle">账户设置</h3>
            <p>管理个人账号与企业服务</p>
          </div>
          <button className="account-close" onClick={onClose} type="button" aria-label="关闭">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="m6 6 12 12M18 6 6 18"/>
            </svg>
          </button>
        </header>

        {/* Content */}
        <section className="account-main">
          {/* Tabs */}
          <div className="account-tabs" role="tablist">
            <button
              className="account-tab"
              role="tab"
              aria-selected={activeTab === 'profile'}
              onClick={() => setActiveTab('profile')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="3.5"/>
                <path d="M5.5 20a6.5 6.5 0 0 1 13 0"/>
              </svg>
              <span>个人信息</span>
            </button>
            <button
              className="account-tab"
              role="tab"
              aria-selected={activeTab === 'security'}
              onClick={() => setActiveTab('security')}
            >
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round">
                <path d="m10 2 7 3v5c0 4-7 8-7 8S3 14 3 10V5l7-3Zm-3 8 2 2 4-5"/>
              </svg>
              <span>登录与安全</span>
            </button>
          </div>

          <div className="account-content">
            {/* ======== 个人信息 Tab ======== */}
            {activeTab === 'profile' && (
              <div className="account-panel" role="tabpanel">

                {/* 个人资料 */}
                <section className="account-profile-section">
                  <div className="account-profile-section-head">
                    <div className="account-profile-section-copy">
                      <h4>个人资料</h4>
                      <p>维护头像、姓名与工作联系方式</p>
                    </div>
                    {!isEditingProfile && (
                      <button className="account-row-btn" onClick={startEditing}>编辑资料</button>
                    )}
                  </div>

                  {isEditingProfile ? (
                    <form onSubmit={handleSaveProfile}>
                      {/* 头像编辑 */}
                      <div className="account-avatar-editor">
                        <button
                          className="account-avatar-picker"
                          type="button"
                          onClick={() => avatarInputRef.current?.click()}
                          title="点击更换头像"
                        >
                          {avatarPreview ? (
                            <img
                              src={avatarPreview}
                              alt="头像预览"
                              style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                            />
                          ) : avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt="当前头像"
                              style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <span
                              className="account-avatar-edit-preview"
                              style={{ background: avatarBg }}
                            >
                              {user?.avatar_text || getAvatarText()}
                            </span>
                          )}
                          <span className="account-avatar-picker-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M4 8h3l1.5-2h7L17 8h3v11H4z"/>
                              <circle cx="12" cy="13" r="3"/>
                            </svg>
                          </span>
                        </button>
                        {/* 隐藏文件输入 */}
                        <input
                          ref={avatarInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleAvatarSelect(file);
                            e.target.value = '';
                          }}
                        />
                        <div className="account-avatar-editor-copy">
                          <b>个人头像</b>
                          <span>支持 JPG、PNG 或 WebP，最大 2 MB</span>
                          <div className="account-avatar-actions">
                            <button
                              className="account-avatar-action"
                              type="button"
                              onClick={() => avatarInputRef.current?.click()}
                            >
                              {avatarFile ? '重新选择' : '选择图片'}
                            </button>
                            {avatarFile && (
                              <button
                                className="account-avatar-action"
                                type="button"
                                style={{ marginLeft: 8 }}
                                onClick={() => { setAvatarPreview(null); setAvatarFile(null); }}
                              >
                                移除
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* 表单字段 */}
                      <div className="account-profile-fields">
                        <div className="eb-field">
                          <label htmlFor="acProfileName">
                            显示名称 <span style={{ color: 'var(--red)', fontSize: 11 }}>必填</span>
                          </label>
                          <input
                            id="acProfileName"
                            type="text"
                            value={profileForm.displayName}
                            onChange={e => setProfileForm(p => ({ ...p, displayName: e.target.value }))}
                            maxLength={40}
                            placeholder="你的显示名称"
                            autoFocus
                          />
                        </div>
                        <div className="eb-field">
                          <label htmlFor="acProfileEmail">工作邮箱</label>
                          <input
                            id="acProfileEmail"
                            type="email"
                            value={profileForm.email}
                            onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))}
                            maxLength={120}
                          />
                          <span className="account-field-meta">用于接收工作通知</span>
                        </div>
                        <div className="eb-field">
                          <label htmlFor="acProfilePhone">手机号码</label>
                          <input
                            id="acProfilePhone"
                            type="tel"
                            value={profileForm.phone}
                            onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                            maxLength={24}
                            placeholder="用于企业联系"
                          />
                        </div>
                      </div>
                      <p className="account-profile-note">联系方式不会改变登录账号。</p>

                      {/* 操作按钮 — 明显，与原型一致 */}
                      <div className="account-form-actions">
                        <button
                          className="btn btn-secondary"
                          type="button"
                          onClick={cancelEditing}
                          disabled={saving}
                        >
                          取消
                        </button>
                        <button
                          className="btn btn-primary"
                          type="submit"
                          disabled={saving}
                        >
                          {saving ? '保存中…' : '保存资料'}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="account-list">
                      <div className="account-list-row account-avatar-row">
                        <span className="account-list-label">头像</span>
                        {avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt="头像"
                            style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <span
                            className="account-profile-avatar"
                            style={{ background: avatarBg, color: '#fff', fontSize: 20, fontWeight: 600 }}
                          >
                            {user?.avatar_text || getAvatarText()}
                          </span>
                        )}
                      </div>
                      <div className="account-list-row">
                        <span className="account-list-label">显示名称</span>
                        <span className="account-list-value">{user?.name || '-'}</span>
                      </div>
                      <div className="account-list-row">
                        <span className="account-list-label">工作邮箱</span>
                        <span className="account-list-value">{user?.email || '-'}</span>
                      </div>
                      <div className="account-list-row">
                        <span className="account-list-label">手机号码</span>
                        <span className={`account-list-value ${user?.phone ? '' : 'account-list-empty'}`}>
                          {user?.phone || '未设置'}
                        </span>
                      </div>
                    </div>
                  )}
                </section>

                {/* 企业身份 */}
                <section className="account-profile-section">
                  <div className="account-profile-section-head">
                    <div className="account-profile-section-copy">
                      <h4>企业身份</h4>
                      <p>团队、职位与权限信息</p>
                    </div>
                    <span className="account-managed-badge">企业维护</span>
                  </div>
                  <div className="account-list">
                    <div className="account-list-row">
                      <span className="account-list-label">所属团队</span>
                      <span className="account-list-value">{permissions?.tenant_name || '企业'}</span>
                    </div>
                    <div className="account-list-row">
                      <span className="account-list-label">职位</span>
                      <span className="account-list-value">{(user as any)?.position || <span className="account-list-empty">未设置</span>}</span>
                    </div>
                    <div className="account-list-row">
                      <span className="account-list-label">账号角色</span>
                      <span className="account-list-value">{getRoleLabel(user?.role)}</span>
                    </div>
                  </div>
                </section>

                {/* 可用模块 */}
                <section className="account-profile-section">
                  <div className="account-profile-section-head">
                    <div className="account-profile-section-copy">
                      <h4>我的可用模块</h4>
                      <p>企业已开通且当前账号已获授权的模块</p>
                    </div>
                  </div>
                  <div className="account-module-summary">
                    {['apps'].map(id => (
                      <span key={id} className="account-module-chip">
                        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                          <path d="m4 10 4 4 8-9"/>
                        </svg>
                        {MODULE_LABELS[id] || id}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {/* ======== 登录与安全 Tab ======== */}
            {activeTab === 'security' && (
              <div className="account-panel" role="tabpanel">
                <section className="account-profile-section">
                  <div className="account-profile-section-head">
                    <div className="account-profile-section-copy">
                      <h4>账号与登录</h4>
                      <p>查看登录账号并管理登录凭据</p>
                    </div>
                    <span className="account-managed-badge">企业账号</span>
                  </div>

                  <div className="account-list">
                    <div className="account-list-row">
                      <div className="account-list-label-wrap">
                        <span className="account-list-label">登录账号</span>
                      </div>
                      <span className="account-list-value">
                        {user?.email || '-'}
                        <small className="account-assigned">由企业分配</small>
                      </span>
                      <span className="account-status">正常</span>
                    </div>

                    <div className="account-list-row">
                      <div className="account-list-label-wrap">
                        <span className="account-list-label">登录密码</span>
                        <span className="account-list-row-hint">定期修改可提升安全性</span>
                      </div>
                      <span className="account-list-value account-muted">已设置</span>
                      {!isChangingPassword && (
                        <button className="btn btn-secondary" style={{ fontSize: 12, padding: '5px 12px', minHeight: 30 }} onClick={() => setIsChangingPassword(true)}>
                          修改密码
                        </button>
                      )}
                    </div>
                  </div>

                  {/* 修改密码表单 */}
                  {isChangingPassword && (
                    <div className="account-password-box">
                      <div className="account-password-fields">
                        <label className="account-field">
                          <span>当前密码</span>
                          <input
                            type="password"
                            value={passwordForm.oldPassword}
                            onChange={e => setPasswordForm(p => ({ ...p, oldPassword: e.target.value }))}
                            autoComplete="current-password"
                          />
                        </label>
                        <label className="account-field">
                          <span>新密码</span>
                          <input
                            type="password"
                            value={passwordForm.newPassword}
                            onChange={e => setPasswordForm(p => ({ ...p, newPassword: e.target.value }))}
                            autoComplete="new-password"
                            minLength={8}
                          />
                        </label>
                        <label className="account-field">
                          <span>确认新密码</span>
                          <input
                            type="password"
                            value={passwordForm.confirmPassword}
                            onChange={e => setPasswordForm(p => ({ ...p, confirmPassword: e.target.value }))}
                            autoComplete="new-password"
                            minLength={8}
                          />
                        </label>
                      </div>
                      <p className="account-help">新密码至少 8 位，需包含大写字母、小写字母和数字（如 Admin123）。</p>
                      <div className="account-form-actions">
                        <button
                          className="btn btn-secondary"
                          type="button"
                          disabled={changingPwd}
                          onClick={() => {
                            setIsChangingPassword(false);
                            setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
                          }}
                        >
                          取消
                        </button>
                        <button
                          className="btn btn-primary"
                          type="button"
                          disabled={changingPwd}
                          onClick={() => handleChangePassword()}
                        >
                          {changingPwd ? '修改中…' : '确认修改'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 退出登录 */}
                  <div className="account-logout">
                    <div>
                      <strong>退出当前账号</strong>
                      <p>退出后需要重新登录才能使用</p>
                    </div>
                    <button className="btn account-logout-btn" type="button" onClick={handleLogout}>
                      退出登录
                    </button>
                  </div>
                </section>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>,
    document.body
  );
}
